import { mdiCreditCardPlusOutline, mdiCreditCardRemove } from '@mdi/js';
import Icon from '@mdi/react';
import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Tooltip,
  Typography
} from '@mui/material';
import { ModalError } from '@vabembarqueja/embarque-ja-shared-components';
import { useEffect, useState } from 'react';
import { PatternFormat } from 'react-number-format';

import { Funcionario } from '../../models/Funcionario.model';
import FuncionarioService from '../../services/Funcionario.service';
import { Colors } from '../../shared/colors';
import DrawerAssociarCartao from '../features/associar-usuario/DrawerAssociarCartao';
import DrawerDesassociarCartao from '../features/associar-usuario/DrawerDesassociarCartao';

import Currency from './Currency';
import { Switch } from './Switch';

interface Props {
  onUpdate: () => void;
  onChecked: (checked: boolean, funcionario: Funcionario) => void;
  contaFuncionario: Funcionario;
}

const obterIniciaisNome = (nome: string) => {
  const palavras = nome.trim().split(/\s+/).filter(Boolean);
  if (!palavras.length) return '?';
  if (palavras.length === 1) return palavras[0].slice(0, 2).toLocaleUpperCase('pt-BR');
  return `${palavras[0][0]}${palavras[palavras.length - 1][0]}`.toLocaleUpperCase('pt-BR');
};

const CardAssociado = ({ contaFuncionario, onChecked, onUpdate }: Props) => {
  const [checked, setChecked] = useState(false);
  const [funcionarioAtivo, setFuncionarioAtivo] = useState(contaFuncionario.ativo);
  const [openModalAssociar, setOpenModalAssociar] = useState(false);
  const [openModalDesassociar, setOpenModalDesassociar] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [openTooltip, setOpenTooltip] = useState(false);
  const [loading, setLoading] = useState(false);

  const abrirModalAssociar = () => setOpenModalAssociar(true);
  const abrirModalDesassociar = () => setOpenModalDesassociar(true);

  const saldoZerado = contaFuncionario.saldoCarteira === 0;

  const fecharModalAssociar = () => {
    onUpdate();
    setOpenModalAssociar(false);
  };

  const fecharModalDesassociar = () => {
    onUpdate();
    setOpenModalDesassociar(false);
  };

  const handleCloseModalError = () => {
    setError(false);
  };
  const handleCloseTooltip = () => {
    setOpenTooltip(false);
  };

  const handleOpenTooltip = () => {
    if (!saldoZerado || !contaFuncionario.usuarioCadastrado) setOpenTooltip(true);
  };

  const alterarStatus = () => {
    setLoading(true);
    setChecked(false);
    const prevValue = funcionarioAtivo;
    FuncionarioService.alterarStatus({
      id: contaFuncionario.id,
      ativo: !funcionarioAtivo
    })
      .then(() => setFuncionarioAtivo(!prevValue))
      .catch((err: Error) => {
        setError(true);
        setErrorMessage(err.message);
        setFuncionarioAtivo(prevValue);
      })
      .finally(() => setLoading(false));
  };

  const cadastroPendente = !funcionarioAtivo || !contaFuncionario.usuarioCadastrado;
  const tooltipText = !contaFuncionario.usuarioCadastrado
    ? 'Essa ação não pode ser realizada para cadastro pendente'
    : 'O saldo deve estar zerado para realizar essa ação';

  useEffect(() => {
    onChecked(checked, contaFuncionario);
  }, [checked, contaFuncionario, onChecked]);
  return (
    <>
      <Grid
        container
        paddingX={{ xs: 1.5, sm: 2 }}
        marginBottom={3}
        paddingY={{ xs: 1.5, sm: 1.25 }}
        borderRadius={1}
        alignItems="center"
        rowSpacing={{ xs: 1.5, md: 0 }}
        sx={{
          bgcolor: Colors.white,
          boxShadow: checked
            ? `0 4px 18px 4px ${Colors.primary.light}`
            : '0 4px 14px rgba(16, 42, 86, 0.16)',
          transition: 'box-shadow 150ms ease'
        }}
      >
        <Grid container item xs={12} md={6} alignItems="center" wrap="nowrap" minWidth={0}>
          <Checkbox
            value={checked}
            checked={checked}
            sx={{ borderRadius: 3 }}
            onChange={(_e, checked) => setChecked(checked)}
            disabled={cadastroPendente}
          />
          <Avatar
            aria-label={`Iniciais de ${contaFuncionario.nome}`}
            sx={{
              bgcolor: Colors.primary.light,
              color: Colors.secondary,
              flexShrink: 0,
              fontSize: 15,
              fontWeight: 600,
              height: 46,
              width: 46,
              ml: 0.5,
              mr: 2
            }}
          >
            {obterIniciaisNome(contaFuncionario.nome)}
          </Avatar>
          <Grid item container flex={1} flexDirection="column" minWidth={0}>
            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
              <Tooltip title={contaFuncionario.nome}>
                <Typography
                  noWrap
                  variant="body1"
                  fontWeight={600}
                  color={Colors.secondary}
                  lineHeight={1.5}
                  sx={{ fontSize: 15, minWidth: 0 }}
                >
                  {contaFuncionario.nome}
                </Typography>
              </Tooltip>
              {!contaFuncionario.usuarioCadastrado && (
                <Chip label="Cadastro pendente" color="warning" size="small" />
              )}
            </Box>
            <Typography variant="body2" color={Colors.gray1} sx={{ overflowWrap: 'anywhere' }}>
              CPF:
              <PatternFormat
                displayType="text"
                value={contaFuncionario.cpf}
                format=" ###.###.###-##"
              />
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={7} md={3} display="flex" alignItems="center" gap={1} minWidth={0}>
          {contaFuncionario.numeroCartaoFisico ? (
            <Box
              p={1}
              display="flex"
              borderRadius={1}
              bgcolor={Colors.gray3}
              sx={{ cursor: 'pointer' }}
              onClick={abrirModalDesassociar}
            >
              <Icon size={1} path={mdiCreditCardRemove} color={Colors.gray1} />
            </Box>
          ) : (
            <Box
              p={1}
              display="flex"
              borderRadius={1}
              onClick={abrirModalAssociar}
              sx={{ cursor: 'pointer' }}
              bgcolor={Colors.primary.main}
            >
              <Icon size={1} color={Colors.white} path={mdiCreditCardPlusOutline} />
            </Box>
          )}
          <Box minWidth={0}>
            <Typography color={Colors.gray1} variant="body2">
              Cartão físico
            </Typography>
            <Typography color={Colors.gray1} variant="body2" sx={{ overflowWrap: 'anywhere' }}>
              {contaFuncionario.numeroCartaoFisico ?? 'Não associado'}
            </Typography>
          </Box>
        </Grid>
        <Grid
          item
          xs={5}
          md={3}
          container
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
        >
          <Typography variant="body2" color={Colors.gray1} textAlign="right">
            Saldo:{' '}
            <Typography
              component="span"
              fontSize="inherit"
              fontWeight={600}
              color={Colors.secondary}
            >
              <Currency value={contaFuncionario.saldoCarteira} />
            </Typography>
          </Typography>
          <Tooltip
            title={tooltipText}
            enterTouchDelay={0}
            placement="top-start"
            open={openTooltip}
            onOpen={handleOpenTooltip}
            onClose={handleCloseTooltip}
          >
            <FormControlLabel
              label="Ativo"
              labelPlacement="start"
              disabled={!saldoZerado || !contaFuncionario.usuarioCadastrado}
              sx={{ color: Colors.gray1 }}
              control={
                loading ? (
                  <Box ml={3} mr={2} mt={2}>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  <Switch checked={funcionarioAtivo} disabled={loading} onChange={alterarStatus} />
                )
              }
            />
          </Tooltip>
        </Grid>
      </Grid>
      <DrawerAssociarCartao
        open={openModalAssociar}
        onClose={fecharModalAssociar}
        funcionario={contaFuncionario}
      />
      <DrawerDesassociarCartao
        open={openModalDesassociar}
        onClose={fecharModalDesassociar}
        funcionario={contaFuncionario}
      />
      <ModalError
        open={error}
        setOpen={setError}
        message={errorMessage}
        onClose={handleCloseModalError}
      />
    </>
  );
};

export default CardAssociado;
